import { z } from 'zod';
import { CurrencySchema, MoneyStringSchema } from './money';

/**
 * 兑换码：后台生成不同面额的码，用户在账单页兑换，额度立即到账（流水类型 redeem）。
 * 同一个码同一个用户只能兑换一次；max_redemptions 是这个码总共能被兑换几次（1 = 一次性卡密，null = 不限）。
 */

/** 去掉空格、连字符等分隔符并转大写（用户可以照着带分隔符的格式输入） */
export const normalizeRedeemCode = (raw: string) => raw.toUpperCase().replace(/[^0-9A-Z]/g, '');

/** 显示用：16 位随机码按 4 位一组加连字符；自定义码原样显示 */
export const formatRedeemCode = (code: string) => (/^[0-9A-Z]{16}$/.test(code) ? code.replace(/(.{4})(?=.)/g, '$1-') : code);

export const REDEEM_CODE_STATUSES = ['active', 'disabled'] as const;
export type RedeemCodeStatus = (typeof REDEEM_CODE_STATUSES)[number];

/** 用户兑换 */
export const RedeemRequestSchema = z.object({ code: z.string().trim().min(4).max(64) });
export type RedeemRequest = z.infer<typeof RedeemRequestSchema>;

export const RedeemResultSchema = z.object({
  amount: z.string(),
  currency: CurrencySchema,
  /** 兑换后的可用额度 */
  available: z.string(),
});
export type RedeemResult = z.infer<typeof RedeemResultSchema>;

/** 后台批量生成 */
export const CreateRedeemCodesSchema = z
  .object({
    amount: MoneyStringSchema.refine((v) => !v.startsWith('-') && Number(v) > 0, '面额必须大于 0'),
    count: z.number().int().min(1).max(1000),
    /** 每个码总共能被兑换几次；null = 不限（同一用户仍只能兑换一次） */
    max_redemptions: z.number().int().min(1).max(1_000_000).nullable(),
    expires_at: z.iso.datetime({ offset: true }).nullable(),
    note: z.string().trim().max(200).optional(),
    /** 自定义码（只在 count = 1 时可用），4–32 位字母数字，不区分大小写 */
    custom_code: z
      .string()
      .trim()
      .transform(normalizeRedeemCode)
      .pipe(z.string().regex(/^[0-9A-Z]{4,32}$/, '自定义码为 4–32 位字母或数字'))
      .optional(),
  })
  .refine((v) => !v.custom_code || v.count === 1, { message: '自定义码只能生成 1 个', path: ['custom_code'] });
export type CreateRedeemCodes = z.input<typeof CreateRedeemCodesSchema>;

export interface AdminRedeemCode {
  id: string;
  /** 显示格式（带分隔符） */
  code: string;
  batch_no: string;
  amount: string;
  currency: 'CNY' | 'USD';
  max_redemptions: number | null;
  redeemed_count: number;
  expires_at: string | null;
  status: RedeemCodeStatus;
  /** 根据状态、过期时间与次数算出的当前可用性 */
  state: 'active' | 'disabled' | 'expired' | 'exhausted';
  note: string | null;
  created_by: string | null;
  created_at: string;
}

export interface CreateRedeemCodesResult {
  batch_no: string;
  codes: AdminRedeemCode[];
}

export interface AdminRedeemRedemption {
  user_id: string;
  user_email: string;
  amount: string;
  created_at: string;
}

export const UpdateRedeemStatusSchema = z.object({ status: z.enum(REDEEM_CODE_STATUSES) });
export const UpdateRedeemBatchStatusSchema = z.object({ batch_no: z.string().min(1).max(64), status: z.enum(REDEEM_CODE_STATUSES) });
